import { useCallback, useEffect, useState } from 'react';

/**
 * Do not change this key. It is already populated in users' browsers — any drift
 * silently empties everyone's "Gần đây" list with no error to notice.
 */
const HISTORY_KEY = 'search-location-history';
const MAX_HISTORY = 5;

export interface HistoryItem {
  code: string;
  name: string;
}

function readHistory(): HistoryItem[] {
  if (typeof window === 'undefined') return [];

  try {
    const raw = window.localStorage.getItem(HISTORY_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];

    return parsed.filter((item): item is HistoryItem => typeof item?.code === 'string' && typeof item?.name === 'string').slice(0, MAX_HISTORY);
  } catch {
    return [];
  }
}

/**
 * Search history, read synchronously on mount rather than in an effect.
 *
 * The consumer only ever mounts inside a portal opened by a user gesture, so the
 * initializer always runs on a hydrated client and sees real storage. That means
 * the chips are correct on the panel's first paint — there is no empty→populated
 * transition and so no layout jump.
 */
export function useSearchHistory() {
  const [history, setHistory] = useState<HistoryItem[]>(readHistory);

  // Cross-tab sync only. The initial read is handled above.
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key === HISTORY_KEY) setHistory(readHistory());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const addHistory = useCallback((item: HistoryItem) => {
    setHistory((prev) => {
      const next = [item, ...prev.filter((entry) => entry.code !== item.code)].slice(0, MAX_HISTORY);
      try {
        window.localStorage.setItem(HISTORY_KEY, JSON.stringify(next));
      } catch {
        // Storage can be full or blocked (Safari private mode). The in-memory
        // list still updates, so the current session behaves correctly.
      }
      return next;
    });
  }, []);

  const clearHistory = useCallback(() => {
    setHistory([]);
    try {
      window.localStorage.removeItem(HISTORY_KEY);
    } catch {
      // as above
    }
  }, []);

  return { history, addHistory, clearHistory };
}

export { HISTORY_KEY, MAX_HISTORY };
